import { cn } from '@/lib/utils/helpers'
import { RENT_PAID_TYPES, RENTAL_STATUSES } from '../constants/residentConstants'
import { RENT_PAID_BADGE_BASE, RENT_PAID_BADGE_TONES } from '../constants/listBadgeStyles'

/**
 * Rent Paid Status badge classes — matches listing screenshot:
 * Paid in Full (green), Not Paid in Full (amber), Not updated (grey).
 */
export function rentPaidStatusBadgeClass(payStatus?: string | null): string {
  const key = String(payStatus || '').trim().toUpperCase()
  if (key === 'PAID') {
    return cn(
      RENT_PAID_BADGE_BASE,
      RENT_PAID_BADGE_TONES.paid,
    )
  }
  if (key === 'NOT_PAID') {
    return cn(
      RENT_PAID_BADGE_BASE,
      RENT_PAID_BADGE_TONES.notPaid,
    )
  }
  if (!key) {
    return cn(
      RENT_PAID_BADGE_BASE,
      RENT_PAID_BADGE_TONES.notUpdated,
    )
  }
  // Forbearance / Partial Payment — neutral outline, still a badge
  return cn(
    RENT_PAID_BADGE_BASE,
    RENT_PAID_BADGE_TONES.neutral,
  )
}

export function rentPaidStatusLabel(payStatus?: string | null): string {
  const key = String(payStatus || '').trim().toUpperCase()
  if (!key) return 'Not updated'
  return (
    RENT_PAID_TYPES[key as keyof typeof RENT_PAID_TYPES] ||
    String(payStatus)
  )
}

/**
 * Normalize rental status tokens from the list API (e.g. `Active`, `ACTIVE`, `Not Started`).
 */
export function normalizeRentalStatusKey(rentalStatus?: string | null): string {
  const raw = String(rentalStatus || '').trim()
  if (!raw) return 'NOT_STARTED'
  const upper = raw.toUpperCase().replace(/\s+/g, '_')
  if (upper === 'NOT_STARTED' || upper === 'NOTSTARTED') return 'NOT_STARTED'
  if (upper === 'ACTIVE') return 'ACTIVE'
  if (upper === 'SKIPPED' || upper === 'SKIP') return 'SKIPPED'
  if (upper === 'EVICTION') return 'EVICTION'
  if (upper === 'CLOSED' || upper === 'CLOSE') return 'CLOSED'
  return upper
}

/**
 * Rental Status text color — matches listing screenshot rules:
 * Not Started (grey), Active (green), Skip / Eviction / Close (red).
 */
export function rentalStatusTextClass(rentalStatus?: string | null): string {
  const key = normalizeRentalStatusKey(rentalStatus)
  if (key === 'ACTIVE') return 'font-medium text-[#1e7e34]'
  if (key === 'SKIPPED' || key === 'EVICTION' || key === 'CLOSED') {
    return 'font-medium text-[#c62828]'
  }
  // NOT_STARTED and anything else
  return 'font-medium text-[#5f6368]'
}

export function rentalStatusLabel(rentalStatus?: string | null): string {
  const key = normalizeRentalStatusKey(rentalStatus)
  return (
    RENTAL_STATUSES[key as keyof typeof RENTAL_STATUSES] ||
    String(rentalStatus || 'Not Started')
  )
}
